import type { ReactNode } from 'react';
import { cn } from '../cn.js';

/**
 * I quattro stati del dato (brief §41).
 *
 * Regola non negoziabile: mai trasformare assenza di dati in dati inventati.
 * Questo componente e' l'unico modo previsto per mostrare uno stato: nessuna
 * schermata scrive "N/D" a mano.
 *
 * Accessibilita': lo stato e' veicolato da glifo + testo, mai dal solo colore.
 */

export type DataStatusValue = 'verified' | 'estimated' | 'unavailable' | 'stale';

interface StatusPresentation {
  readonly glyph: string;
  readonly defaultLabel: string;
  readonly color: string;
  readonly srPrefix: string;
}

const PRESENTATION: Record<DataStatusValue, StatusPresentation> = {
  verified: {
    glyph: '✓',
    defaultLabel: 'verificato',
    color: 'text-[var(--data-verified)]',
    srPrefix: 'Dato verificato:',
  },
  estimated: {
    glyph: '≈',
    defaultLabel: 'stimato',
    color: 'text-[var(--data-estimated)]',
    srPrefix: 'Valore stimato:',
  },
  unavailable: {
    glyph: '○',
    defaultLabel: 'non disponibile',
    color: 'text-[var(--data-unavailable)]',
    srPrefix: 'Dato non disponibile:',
  },
  stale: {
    glyph: '!',
    defaultLabel: 'da verificare',
    color: 'text-[var(--data-stale)]',
    srPrefix: 'Dato da verificare:',
  },
};

export interface DataBadgeProps {
  readonly status: DataStatusValue;
  /** Testo mostrato accanto al glifo. Se assente si usa l'etichetta di default. */
  readonly label?: string;
  readonly className?: string;
}

export function DataBadge({ status, label, className }: DataBadgeProps): ReactNode {
  const presentation = PRESENTATION[status];
  const text = label ?? presentation.defaultLabel;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 text-[13px] leading-5',
        presentation.color,
        className,
      )}
    >
      <span aria-hidden="true" className="font-semibold">
        {presentation.glyph}
      </span>
      <span className="sr-only">{presentation.srPrefix}</span>
      <span>{text}</span>
    </span>
  );
}

/** Etichetta standard quando gli orari di apertura non sono noti. */
export const OPENING_HOURS_UNAVAILABLE_LABEL =
  'Orari non disponibili — verifica prima di partire';
